import { selector } from "recoil";
import { CIFType, geoLevelProps, selPOICatState } from "../states"
import { GEO_LEVELS, MetricKey, RouteConstants } from "../constants";
import { ColumnDef } from "@tanstack/react-table";
import React from "react";
import { LinkWithIcon } from "../ui-elements";
import highlightedPoI from '../utils/images/Highlighted PoI.svg';

export const geoStateDefaultSelector = selector({
    key: 'geoStateDefaultSelector',
    get: () => {
        const params: any = { country: '1' };
        if (!window.location.search) return params;
        let searchParams = window.location.search.slice(1);
        const geoKeys = ['country', 'state', 'district', 'sub_district', 'village'];
        searchParams?.toString().split('&').forEach((param) => {
            let [key, value]: any = param.split('=');
            if (geoKeys.includes(key) && value) {
                params[key] = value.toString();
            }
        });
        return params;
    },
});

const createImage = (url: any) =>
    new Promise((resolve, reject) => {
        const image = new Image()
        image.addEventListener('load', () => resolve(image))
        image.addEventListener('error', (error) => reject(error))
        image.setAttribute('crossOrigin', 'anonymous') // needed to avoid cross-origin issues on CodeSandbox
        image.src = url
    })

const getRadianAngle = (degreeValue: any) => {
    return (degreeValue * Math.PI) / 180
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
const rotateSize = (width: any, height: any, rotation: any) => {
    const rotRad = getRadianAngle(rotation)

    return {
        width:
            Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
        height:
            Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    }
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
export const getCroppedImg = async (
    imageSrc: any,
    pixelCrop: { width: number; height: number; x: number; y: number; },
    rotation = 0,
    flip = { horizontal: false, vertical: false }
) => {
    const image: any = await createImage(imageSrc);
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
        return null
    }

    const rotRad = getRadianAngle(rotation)

    // calculate bounding box of the rotated image
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
        image.width,
        image.height,
        rotation
    )

    // set canvas size to match the bounding box
    canvas.width = bBoxWidth
    canvas.height = bBoxHeight

    // translate canvas context to a central location to allow rotating and flipping around the center
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2)
    ctx.rotate(rotRad)
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1)
    ctx.translate(-image.width / 2, -image.height / 2)

    // draw rotated image
    ctx.drawImage(image, 0, 0)

    const croppedCanvas = document.createElement('canvas')

    const croppedCtx = croppedCanvas.getContext('2d')

    if (!croppedCtx) {
        return null
    }

    // Set the size of the cropped canvas
    croppedCanvas.width = pixelCrop.width
    croppedCanvas.height = pixelCrop.height

    // Draw the cropped image onto the new canvas
    croppedCtx.drawImage(
        canvas,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    )

    // As Base64 string
    // return croppedCanvas.toDataURL('image/jpeg');

    // As a blob
    return new Promise((resolve, reject) => {
        croppedCanvas.toBlob((file) => {
            if (file) {
                return resolve(URL.createObjectURL(file));
            }
        }, 'image/jpeg')
    })
}

export const initialGenerator = (value: string) => {
    if (value) {
        let initial = value.split(' ');
        if (initial[1]) {
            return initial?.[0][0]?.toUpperCase() + initial?.[1][0]?.toUpperCase();
        } else {
            return initial?.[0][0]?.toUpperCase();
        }
    } else {
        return '';
    }
}

const hRange = [0, 360];
// const sRange = [0, 100];
// const lRange = [0, 100];
const sRange = [0, 80];
const lRange = [0, 100];

const getHashOfString = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);
    return hash;
};

const normalizeHash = (hash: number, min: number, max: number) => {
    return Math.floor((hash % (max - min)) + min);
};

const HSLtoString = (hsl: [number, number, number]) => {
    return `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`;
};

export const generateHSL = (name: string) => {
    const hash = getHashOfString(name);
    const h = normalizeHash(hash, hRange[0], hRange[1]);
    const s = normalizeHash(hash, sRange[0], sRange[1]);
    const l = normalizeHash(hash, lRange[0], lRange[1]);
    const hsl = HSLtoString([h, s, l]);
    return hsl;
};

export const getSortedTable = (data: any, item: any, order: string) => {
    let sortedTable = data.slice().sort((a: any, b: any) => {
        const aValue = item.KEY === 'state' ? a['geo_hierarchy']?.[1]?.geo_name : a[item.KEY];
        const bValue = item.KEY === 'state' ? b['geo_hierarchy']?.[1]?.geo_name : b[item.KEY];
        if (!aValue && !bValue) {
            return 0;
        } else if (!aValue) {
            return 1;
        } else if (!bValue) {
            return -1;
        } else if (typeof aValue === 'string' && typeof bValue === 'string') {
            return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
            return order === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
            return 0;
        }
    });
    return sortedTable;
}

export const getNumberWithZero = (value: null | number | string) => {
    if (value !== undefined && value !== null) {
        const numericValue = typeof value === 'string' ? parseInt(value, 10) : value;
        if (numericValue < 10) {
            return '0' + numericValue.toString();
        }
        return numericValue.toString();
    }
    return '--';
};

export const getSelectedGeoName = (list: any, geoId: any, geo_level_name: string) => {
    // if (!list) return 'Select ' + formatGeoLevelString(geo_level_name);
    if (!list) return;

    const foundItem = list.find((item: any) => item.geoId === Number(geoId));
    if (foundItem) return foundItem.geoName;
    // return 'Select ' + formatGeoLevelString(geo_level_name);
    return formatGeoLevelString(geo_level_name);
}

export const getPoiIconColor = (cifType: CIFType[], selCats: number[], catId: number) => {
    if (cifType.toString() === 'PoI') {
        if (selCats.includes(catId)) return 'bg-poi-highlight text-white';
        else return 'bg-white color-poi';
    } else {
        return 'bg-poi color-poi';
    }
}

export const getNonPoiIconColor = (cifType: CIFType[]) => {
    if (cifType.toString() === 'CTV') return 'bg-white color-ctv';
    else if (cifType.toString() === 'EH') return 'bg-white color-eh';
    return 'bg-ctv color-ctv';
}

export const getNonPoiBgColor = (cifType: CIFType[]) => {
    if (cifType.toString() === 'CTV') return 'bg-ctv';
    else if (cifType.toString() === 'EH') return 'bg-eh';
    return 'bg-white';
}

export interface GeoBase {
    geo_id: number;
    geo_value: string;
    code: string
}

export interface State extends GeoBase { }

export interface District extends GeoBase {
    parent: {
        geo_id: number;
        geo_value: string;
        code: string;
    };
}

export interface SubDistrict extends GeoBase {
    parent: {
        geo_id: number;
        geo_value: string;
        code: string;
    };
    grand_parent: {
        geo_id: number;
        geo_value: string;
        code: string;
    };
}

export interface Pincode {
    geo_id: string;
    geo_value: string;
    code: string;
    parent: any;
    grand_parent: any;
    // (add any more fields you need)
}

export interface Village {
    geo_id: string;
    geo_value: string;
    code: string;
    parent: any;
    grand_parent: any;
    // (add any more fields you need)
}


// export const transformData = (data: any[]): { states: State[]; districts: District[]; sub_districts: SubDistrict[] } => {
//     const result = {
//         states: [] as State[],
//         districts: [] as District[],
//         sub_districts: [] as SubDistrict[],
//     };

//     function traverse(node: any, parent: any = null, grandParent: any = null) {
//         if (!node) return;

//         // Process state
//         if (!parent && !grandParent) {
//             result.states.push({ geo_id: node.geo_id, geo_value: node.geo_value, code: node.code });
//         }

//         // Process district
//         if (parent && !grandParent) {
//             result.districts.push({
//                 geo_id: node.geo_id,
//                 geo_value: node.geo_value,
//                 code: node.code,
//                 parent: { geo_id: parent.geo_id, geo_value: parent.geo_value, code: parent.code },
//             });
//         }

//         // Process sub_district
//         if (grandParent) {
//             result.sub_districts.push({
//                 geo_id: node.geo_id,
//                 geo_value: node.geo_value,
//                 code: node.code,
//                 parent: { geo_id: parent.geo_id, geo_value: parent.geo_value, code: parent.code },
//                 grand_parent: { geo_id: grandParent.geo_id, geo_value: grandParent.geo_value, code: grandParent.code },
//             });
//         }

//         // Recursively process children
//         if (node.children && node.children.length > 0) {
//             node.children.forEach((child: any) => traverse(child, node, parent));
//         }
//     }

//     // Start traversal
//     data.forEach((item) => traverse(item));
//     return result;
// };

export const transformData = (data: any[]): {
    states: State[];
    districts: District[];
    sub_districts: SubDistrict[];
    pincodes: Pincode[];
} => {
    const result = {
        states: [] as State[],
        districts: [] as District[],
        sub_districts: [] as SubDistrict[],
        pincodes: [] as Pincode[],
    };

    function traverse(node: any, parent: any = null, grandParent: any = null) {
        if (!node) return;

        if (!parent && !grandParent) {
            // State
            result.states.push({ geo_id: node.geo_id, geo_value: node.geo_value, code: node.code });
        }

        if (parent && !grandParent) {
            // District
            result.districts.push({
                geo_id: node.geo_id,
                geo_value: node.geo_value,
                code: node.code,
                parent: { geo_id: parent.geo_id, geo_value: parent.geo_value, code: parent.code },
            });
        }

        if (grandParent) {
            if (node.geotype === "sub_district") {
                result.sub_districts.push({
                    geo_id: node.geo_id,
                    geo_value: node.geo_value,
                    code: node.code,
                    parent: { geo_id: parent.geo_id, geo_value: parent.geo_value, code: parent.code },
                    grand_parent: { geo_id: grandParent.geo_id, geo_value: grandParent.geo_value, code: grandParent.code },
                });
            } else if (node.geotype === "pincode") {
                result.pincodes.push({
                    geo_id: node.geo_id,
                    geo_value: node.geo_value,
                    code: node.code,
                    parent: { geo_id: parent.geo_id, geo_value: parent.geo_value, code: parent.code },
                    grand_parent: { geo_id: grandParent.geo_id, geo_value: grandParent.geo_value, code: grandParent.code },
                });
            }
        }

        if (node.children && node.children.length > 0) {
            node.children.forEach((child: any) => traverse(child, node, parent));
        }
    }

    data.forEach((item) => traverse(item));
    return result;
};


export const formatGeoLevelString = (input: string): string => {
    if (!input) return '';
    if (input === 'sub_district') return 'Sub-Region';
    const placeholder = input
        .replace(/_/g, ' - ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
        .replace(/\s+/g, '');

    // if (input === 'sub_district') return placeholder + '/Pin Code';
    return placeholder;
}

export const joinGeoNames = (item: any, geo_level: string) => {
    if (!item.grand_parent && !item.parent) {
        return `${item.geo_value} ${item.code ? `(${item.code})` : ''}`;
    } else if (!item.grand_parent && item.parent) {
        return `${item.geo_value} - ${item.parent.geo_value} ${item.parent.code ? `(${item.parent.code})` : ''}`;
    } else if (item.grand_parent && item.parent) {
        if (geo_level !== 'Pincodes') {
            return `${item.geo_value} - ${item.parent.geo_value} - ${item.grand_parent.geo_value} ${item.grand_parent.code ? `(${item.grand_parent.code})` : ''}`;
        }
        else {
            return `${item.geo_value} - ${item.grand_parent.geo_value} ${item.grand_parent.code ? `(${item.grand_parent.code})` : ''}`;
        }
    } else {
        return '';
    }
}

export const getSearchParamsAsObject = () => {
    const searchParams = new URLSearchParams(window.location.search);
    let paramsObject: Partial<geoLevelProps> = {};

    searchParams.forEach((value, key) => {
        if (key in paramsObject) {
            paramsObject[key as keyof geoLevelProps] = value;
        }
    });

    if (Object.keys(paramsObject).length === 0) {
        paramsObject = { country: '1' };
    }

    return paramsObject as geoLevelProps;
};

export const checkIsBrandSelected = (branchId: any, selBrands: any[]) => {
    if (!selBrands || selBrands.length === 0) return false;
    const found = selBrands.flatMap(company => company.branches).find(branch => branch.branchId === branchId);
    if (found) return true;
    else return false;
}

export const getHoverLevel = (geoLevel: string, selectedGeo: geoLevelProps, selectedTab: number) => {
    const { country, state, district, sub_district } = selectedGeo;
    const geo_id = sub_district || district || state || country;

    if (geo_id == district && geoLevel === "district") return false;

    const shouldStyle =
        (geo_id === country && geoLevel === 'state') ||
        (geo_id === state && geoLevel === 'district') ||
        (geo_id === district && ((geoLevel === 'sub_district' && selectedTab === 4) || ((geoLevel === 'pincode' && selectedTab === 5)))) ||
        (geo_id === sub_district && geoLevel === 'village')
    return shouldStyle;
}

// Convert hex color to RGBA array for DeckGL
export const hexToRGBA = (hex: string, alpha: number = 255): [number, number, number, number] => {
    // Remove the # if present
    hex = hex.replace('#', '');

    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return [r, g, b, alpha];
};

export const getDeckFillColor = (selectedGeo: any, selectedTab: number, geoLevel: string, colorProperties: any, cifType: any, geoId: any): [number, number, number, number] => {
    const { country, state, district, sub_district, village } = selectedGeo;
    const geo_id = village || sub_district || district || state || country;

    if (village === geo_id) return hexToRGBA('#FFFFFF', 0);

    if (sub_district === geo_id && (geoLevel === 'sub_district' || geoLevel === 'pincode')) return hexToRGBA('#FFFFFF', 0);

    if (
        (district === geo_id && selectedTab === 5 && geoLevel === "sub_district") ||
        (district === geo_id && selectedTab === 4 && geoLevel === "pincode") ||
        (district === geo_id && geoLevel === "district") ||
        (state === geo_id && geoLevel === "state")
    ) return hexToRGBA('#FFFFFF', 0);

    const fillColor = cifType.length > 0 && colorProperties?.length > 0 ?
        colorProperties.find((item: any) => item.geoId === geoId)?.color || '#FFFFFF' : '#FFFFFF';

    return hexToRGBA(fillColor, 255);
};

export const getDeckFillOpacity = (
    selectedGeo: any,
    selectedTab: number,
    geoLevel: string,
    // showSubRegionBouds: boolean | undefined,
    // metrics: number
) => {
    const { country, state, district, sub_district, village } = selectedGeo;
    const geo_id = village || sub_district || district || state || country;

    if (geoLevel === 'state' || village) return 0;

    // if (geo_id === sub_district) {
    //     if (!showSubRegionBouds || metrics === 0) return 0;
    //     if (showSubRegionBouds && (geoLevel === 'sub_district' || geoLevel === 'pincode')) return 0;
    // }

    if (
        (district === geo_id && selectedTab === 5 && geoLevel === "sub_district") ||
        (district === geo_id && selectedTab === 4 && geoLevel === "pincode") ||
        (district === geo_id && geoLevel === "district")
    ) return 0;

    return 1;
};

// export const getDeckStrokeWeight = (selectedGeo: any, selectedTab: number, geoLevel: string, showSubRegionBouds?: boolean) => {
//     const { country, state, district, sub_district, village } = selectedGeo;
//     const geo_id = village || sub_district || district || state || country;

//     if (village) return [0, 0, 0, 255];

//     if (geoLevel === 'state') return [255, 255, 255, 0];

//     // if (geo_id === sub_district) {
//     //     if (showSubRegionBouds && geoLevel === 'village') return 0.35;
//     //     if (!showSubRegionBouds && (geoLevel === 'sub_district' || geoLevel === 'pincode')) return 1;
//     //     if (showSubRegionBouds && (geoLevel === 'sub_district' || geoLevel === 'pincode')) return 0;
//     // }

//     if ((geo_id === country && geoLevel === 'district') || state === geo_id) return [0, 0, 0, 89];

//     if (geo_id === district) {
//         if ((selectedTab === 5 && geoLevel === "pincode") || (selectedTab === 4 && geoLevel === "sub_district")) return [0, 0, 0, 89];
//     }

//     if (geo_id === sub_district && (geoLevel === "village")) return [0, 0, 0, 89];
//     return [255, 255, 255, 0];
// };

export const getGMapFillColor = (selectedGeo: any, selectedTab: number, geoLevel: string, defaultFillColor: string, totalOverlayCount?: number) => {
    const grayColor = totalOverlayCount && totalOverlayCount >= 1 ? '#BEBEBE' : '#FFFFFF';

    if (selectedGeo.village) return grayColor;

    if (
        (selectedGeo.district && selectedTab === 5 && geoLevel === "sub_district") ||
        (selectedGeo.district && selectedTab === 4 && geoLevel === "pincode")
    ) return grayColor;

    return defaultFillColor;
};

export const getDeckMapFillColor = (selectedGeo: any, selectedTab: number, geoLevel: string, defaultFillColor: string, showSubRegionBouds: boolean | undefined, metrics: number, catchment: string | null, totalOverlayCount?: number): [string, number] => {
    if(geoLevel=="hex") return [defaultFillColor, 255]
    const currGeoLevel = getCurrGeoLevelName(selectedGeo);
    const grayColor =   totalOverlayCount && totalOverlayCount >= 1 ? '#BEBEBE' : '#FFFFFF';
    if (catchment) {
        if (geoLevel === 'catchment') return [grayColor, 0];
        else if (showSubRegionBouds) return [defaultFillColor, 255];
        else return [grayColor, 0];
    }

    if (selectedGeo.village) return [grayColor, 0];

    if (currGeoLevel === 'sub_district') {
        if (geoLevel === 'pincode') return [grayColor, 0];
        if ((!showSubRegionBouds || metrics === 0) && geoLevel === 'village') return [grayColor, 0];
    }

    if (
        (selectedGeo.district && selectedTab === 5 && geoLevel === "sub_district") ||
        (selectedGeo.district && selectedTab === 4 && geoLevel === "pincode")
    ) return [grayColor, 0];

    return [defaultFillColor, 255];
};

export const getGMapFillOpacity = (
    selectedGeo: any,
    selectedTab: number,
    geoLevel: any,
    showSubRegionBouds: boolean | undefined,
    metrics: number,
    catchments: boolean,
) => {
    const currGeoLevel = getCurrGeoLevelName(selectedGeo);

    if (catchments && geoLevel === 'catchment') return 0.55;

    if (currGeoLevel === 'country' && geoLevel === 'state') return 0;

    if (currGeoLevel === 'district') {
        if ((selectedTab === 4 && geoLevel === 'sub_district') || (selectedTab === 5 && geoLevel === 'pincode')) return 1;
        else return 0;
    }

    if (currGeoLevel === 'sub_district') {
        if (geoLevel === 'pincode') return 0;
        if ((!showSubRegionBouds || metrics === 0) && geoLevel === 'village') return 0;
    }

    if (geoLevel !== currGeoLevel) return 1;

    return 0;
};

export const getGMapStrokeColor = (selectedGeo: any, geoLevel: string, adjacency: boolean) => {
    const currGeoLevel = getCurrGeoLevelName(selectedGeo);

    if (geoLevel === currGeoLevel) {
        if (adjacency) return 'rgba(0, 0, 0, 0.3)';
        else return 'rgba(0, 0, 0, 1)';
    } else {
        if (currGeoLevel === 'sub_district' && geoLevel === 'pincode') {
            if (adjacency) return 'rgba(0, 0, 0, 0.3)';
            else return 'rgba(0, 0, 0, 1)';
        }
        if (adjacency) return 'rgba(125, 125, 125, 1)';
        else return 'rgba(0, 0, 0, 1)';
    }
}

export const getGMapStrokeWeight = (selectedGeo: any, selectedTab: number, geoLevel: string, geoId: number, adjacency: boolean, showSubRegionBouds?: boolean) => {
    const currGeoLevel = getCurrGeoLevelName(selectedGeo);

    if (geoLevel === 'state' && currGeoLevel === 'country') return 1.25;

    if (geoLevel === currGeoLevel) return 1.5;

    if (currGeoLevel === 'sub_district' && geoLevel === 'pincode') return 1.5;

    if (currGeoLevel === 'sub_district' && geoLevel === 'village' && !showSubRegionBouds) return 0;

    if ((currGeoLevel === 'district' && selectedTab === 4 && geoLevel === 'pincode') ||
        (currGeoLevel === 'district' && selectedTab === 5 && geoLevel === 'sub_district')
    ) return 0;

    return 0.5;
};

export const getFeatureZIndex = (selectedGeo: any, selectedTab: number, geoLevel: any) => {
    const currGeoLevel = getCurrGeoLevelName(selectedGeo);

    if (currGeoLevel === 'country' && geoLevel === 'state') return true;
    if (currGeoLevel === geoLevel) return true;
    if (currGeoLevel === 'sub_district' && geoLevel === 'pincode') return true;
    return false;
}

export const getHoverClickable = (selectedGeo: any, selectedTab: number, geoLevel: any, showSubRegionBouds: boolean) => {
    const currGeoLevel = getCurrGeoLevelName(selectedGeo);

    if (currGeoLevel === 'country' && geoLevel === 'state') return true;

    if (currGeoLevel === 'district') {
        if (selectedTab === 4 && geoLevel === 'sub_district') return true;
        if (selectedTab === 5 && geoLevel === 'pincode') return true;
        return false;
    }
    if (currGeoLevel === 'sub_district') {
        if (geoLevel === 'pincode') return false;
        if (!showSubRegionBouds) return false;
        if (showSubRegionBouds && geoLevel !== 'village') return false;
        if (showSubRegionBouds && geoLevel === 'village') return true;
    }

    if (currGeoLevel !== geoLevel) return true;
    return false;
}

export const getDeckStrokeColor = (selectedGeo: any, geoLevel: string, adjacency: boolean, catchment: string | null, showSubRegionBouds: boolean) => {
    if (catchment) {
        if (geoLevel === 'catchment') return [0, 0, 0, 255];
        else if (!showSubRegionBouds) return [0, 0, 0, 0];
    }

    const currGeoLevel = getCurrGeoLevelName(selectedGeo);
    if (geoLevel === currGeoLevel) {
        if (adjacency) return [100, 100, 100, 100];
        else return [0, 0, 0, 255];
    } else {
        if (currGeoLevel === 'sub_district' && geoLevel === 'pincode') {
            if (adjacency) return [100, 100, 100, 100];
            else return [0, 0, 0, 255];
        }
        if (adjacency) return [100, 100, 100, 100];
        else return [0, 0, 0, 255];
    }
};

export const getDeckStrokeWeight = (selectedGeo: any, selectedTab: number, geoLevel: string, geoId: number, adjacency: boolean, showSubRegionBouds: boolean, catchment: number | undefined) => {
    if (catchment && !showSubRegionBouds) return 0;

    const currGeoLevel = getCurrGeoLevelName(selectedGeo);

    if (geoLevel === 'state' && currGeoLevel === 'country') return 0;

    if (geoLevel === currGeoLevel) return 1.5;

    if (currGeoLevel === 'sub_district' && geoLevel === 'pincode') return 1.5;

    if (currGeoLevel === 'sub_district' && geoLevel === 'village' && !showSubRegionBouds) return 0;

    if ((currGeoLevel === 'district' && selectedTab === 4 && geoLevel === 'pincode') ||
        (currGeoLevel === 'district' && selectedTab === 5 && geoLevel === 'sub_district')
    ) return 0;

    return adjacency ? 0.65 : 0.65;
};

const distanceFromMapTopToPoint = (boundary: MapBoundary | undefined, zoom: number, lat: number) => {
    if (!boundary) return 0;

    const topLat = boundary.north;
    const bottomLat = boundary.south;

    const latDiff = topLat - bottomLat;

    const latFromTop = topLat - lat;

    const mapHeightInPixels = 256 * Math.pow(2, zoom);
    const distanceFromTopInPixels = (latFromTop / latDiff) * mapHeightInPixels;

    return distanceFromTopInPixels;
};

type MapBoundary = {
    north: number;
    south: number;
    east: number;
    west: number;
};

export const getUpdatedPosition = (lat: number, lng: number, map: any) => {
    if (!map || !map.getBounds()) return { lat, lng };

    const boundary = map.getBounds()?.toJSON() as MapBoundary;

    const distance = distanceFromMapTopToPoint(boundary, map.getZoom() || 1, lat);
    let latOffset = 0;

    if (distance < 8500) {
        const pixelOffset = 50;
        const scale = Math.pow(2, map.getZoom() || 1);
        const projection = map.getProjection();

        if (projection) {
            const point = projection.fromLatLngToPoint(new window.google.maps.LatLng(lat, lng)) as google.maps.Point;

            if (point) {
                const adjustedPoint = new google.maps.Point(point.x, point.y + pixelOffset / scale);

                const adjustedLatLng = projection.fromPointToLatLng(adjustedPoint);
                if (adjustedLatLng) {
                    latOffset = adjustedLatLng.lat() - lat || 0;
                }
            }
        }
    }

    return { lng, lat: lat + latOffset };
};

export const getPoiCount = (cat: any) => {
    if (!cat || !cat.totalPoiCont) return '--';
    if (!cat.selectedCatPoicount || cat.totalPoiCont === cat.selectedCatPoicount) return cat.totalPoiCont;
    else return `${cat.selectedCatPoicount}/${cat.totalPoiCont}`;
}

export const getGeoKeyName = (selectedGeo: geoLevelProps) => {
    const { country, state, district, sub_district } = selectedGeo;
    const geo_id = sub_district || district || state || country;

    const key = (Object.keys(selectedGeo) as Array<keyof typeof selectedGeo>).find(
        key => Number(selectedGeo[key]) === Number(geo_id)
    ) || '';
    if (key === 'state') return 'Nation';
    else return key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
};

export const formatLineChartYAxisLabel = (value: any, currency: string) => {
    const formatValue = (formattedValue: any) => {
        return formattedValue.endsWith('.0') ? formattedValue.slice(0, -2) : formattedValue;
    };

    if (currency === 'USD') {
        if (value >= 1e9) {
            return formatValue((value / 1e9).toFixed(1)) + 'B'; // Billion
        } else if (value >= 1e6) {
            return formatValue((value / 1e6).toFixed(1)) + 'M'; // Million
        } else if (value >= 700000) {
            return formatValue((value / 1e6).toFixed(1)) + 'M'; // Convert large K to M
        } else if (value >= 1000) {
            return formatValue((value / 1e3).toFixed(1)) + 'K'; // Keep small K
        }
    } else if (currency === 'INR') {
        if (value >= 1e7) {
            return formatValue((value / 1e7).toFixed(1)) + 'Cr'; // Crore
        } else if (value >= 1e5) {
            return formatValue((value / 1e5).toFixed(1)) + 'L'; // Lakh
        } else if (value >= 70000) {
            return formatValue((value / 1e5).toFixed(1)) + 'L'; // Convert large K to L
        } else if (value >= 1000) {
            return formatValue((value / 1e3).toFixed(0)) + 'K'; // Keep small K
        }
    }

    return value.toString();
};

export const setDocumentTitle = (value?: any) => {
    if (value) {
        document.title = value + ' | EPIC Intelligence';
    } else {
        document.title = 'EPIC Intelligence';
    }
}

export const createSearchParams = (paramsObj: Record<string, string | null>) => {
    const searchParams = new URLSearchParams();

    Object.entries(paramsObj).forEach(([key, value]) => {
        if (value) {
            searchParams.append(key, value);
        }
    });

    return searchParams.toString();
};

export const getCurrentGeo = (dropdown: any, geoCode: number, selectedGeo: string) => {
    if (!dropdown || !geoCode || !selectedGeo) return;

    const level = Object.entries(selectedGeo)?.filter(([_, value]) => value && value !== "").at(-1)?.[0] ?? null;

    const listName = GEO_LEVELS.find(item => item.key === level)?.list;

    const found = dropdown[listName as any]?.dropDownData?.find((item: any) => item.geoId === Number(geoCode));

    return found;
}

export const createMarkerChild = (type: 'poi' | 'mbp', poi: any, map: google.maps.Map | null, isSubDistrict: false | true, pathname: string) => {
    let content, zIndex;


    if (type === 'poi') {
        content = document.createElement("div");
        content.className = `p-0 m-0  ${poi.isSelected ? 'selected-poi-highlight poi-circle-highlighted' : 'non-selected-poi-highlights poi-circle'}`;

        zIndex = poi.isSelected ? 155 : 150;
    } else if (type === 'mbp') {
        content = document.createElement('img');
        content.src = poi.icon;
        content.alt = poi.branch_name;
        content.className = `${getIconClassName(pathname, isSubDistrict)}`;

        zIndex = 160;
    }

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: poi?.latitude, lng: poi?.longitude },
        map,
        content,
        zIndex
    });

    return marker;
}

const getIconClassName = (pathname: string, isSubDistrict: boolean) => {
    if (pathname !== RouteConstants.root) return 'pip-star';
    else if (isSubDistrict) return 'presence-star-subdistrict';
    else return 'presence-star';
}

export const normalizeOption = (option: any, labelKey: any, valueKey: any) => {
    if (typeof option === 'object' && option !== null) {
        return {
            label: labelKey ? option[labelKey] : option.label,
            value: valueKey ? option[valueKey] : option.value,
        };
    }
    return {
        label: option,
        value: option,
    };
};

const unorderedArrayEqual = (a: any[], b: any[]): boolean => {
    if (a.length !== b.length) return false;
    const aSorted = [...a].sort();
    const bSorted = [...b].sort();
    return aSorted.every((val, i) => deepEqual(val, bSorted[i]));
}

const isPrimitiveArray = (arr: any[]): boolean => {
    return arr.every(item => ['string', 'number', 'boolean'].includes(typeof item));
}

export const deepEqual = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
        if (!Object.prototype.hasOwnProperty.call(b, key)) return false;

        const aVal = a[key];
        const bVal = b[key];

        if (Array.isArray(aVal) && Array.isArray(bVal)) {
            const unordered = isPrimitiveArray(aVal) && isPrimitiveArray(bVal);
            if (unordered) {
                if (!unorderedArrayEqual(aVal, bVal)) return false;
            } else {
                if (!deepEqual(aVal, bVal)) return false;
            }
        } else {
            if (!deepEqual(aVal, bVal)) return false;
        }
    }

    return true;
}

export const checkIsChecked = (configs: any) => {
    const updated = configs.map((config: any) => {
        return {
            ...config,
            status: checkIsKeyThere(config.status),
            parameters: checkIsKeyThere(config.parameters, 'parameters'),
            isSelected: config.hasOwnProperty('isSelected') ? config.isSelected : true,
            selectedStart: config.presence_start,
            selectedEnd: config.presence_end
        }
    });
    return updated;
}

const checkIsKeyThere = (obj: any, key?: string) => {
    return obj.map((item: any, index: number) => {
        if (key === 'parameters') {
            return {
                ...item,
                options: checkIsKeyThere(item.options),
                values: checkIsKeyThere(item.values, 'values'),
                isSelected: item.hasOwnProperty('isSelected') ? item.isSelected : true,
            }
        } else if (key === 'values') {
            return {
                ...item,
                isSelected: item.hasOwnProperty('isSelected') ? item.isSelected : (index === 0 ? true : false),
                selectedYear: item.year[0],
            }
        } else {
            return {
                ...item,
                isSelected: item.hasOwnProperty('isSelected') ? item.isSelected : true,
            }
        }
    })
}
export const formatNumberWithUnit = (input: any) => {
    if (!input) return;

    const trimmed = input.trim();
    // Add CR and L (for Lakh) or any other you expect
    const match = trimmed.match(/^([\d.]+)\s*(K|Mn|Bn|Cr|L|k|l|B|M|n|b)?$/);

    if (!match) return input; // If format doesn't match, return as-is

    const [, numStr, unit = ""] = match;
    const num = parseFloat(numStr);

    // Check if decimal part is .00
    if (num % 1 === 0) {
        return `${Math.floor(num)} ${unit}`.trim();
    } else {
        return `${num} ${unit}`.trim();
    }
};

export const isPayloadEqual = (data: any, geoId: any, selCategoryList: selPOICatState, selCurrency: any, cifType: CIFType[], selMetric?: { key: any; ismetric: any; }[]) => {
    if (!data) return false;

    const isCatsEqual = JSON.stringify(data.cat) === JSON.stringify(selCategoryList);

    const isEqual = (isCatsEqual && data.currency === selCurrency && data.geoId === Number(geoId));

    if (selMetric) {
        const isMetricEqual = JSON.stringify([...selMetric]?.sort((a, b) => a.key.localeCompare(b.key))) === JSON.stringify([...data.metric_type]?.sort((a, b) => a.key.localeCompare(b.key)))
        return isEqual && isMetricEqual;
    } else {
        const isCifTypeEqual = [...cifType].sort().toString() === [...data.type].sort().toString();
        return isEqual && isCifTypeEqual;
    }
}

export const getCountOfChangedRanges = (node: any) => {
    if (!node || node.isdisable) return 0;
    let count = 0;

    // Check if this node's rangeMin or rangeMax differs from min/max
    if (
        typeof node.min === 'number' &&
        typeof node.max === 'number' &&
        (node.rangeMin !== undefined && node.rangeMin !== node.min ||
            node.rangeMax !== undefined && node.rangeMax !== node.max)
    ) {
        count += 1;
    }

    // Recursively check child_overlay
    if (Array.isArray(node.child_overlay)) {
        for (const child of node.child_overlay) {
            count += getCountOfChangedRanges(child);
        }
    }

    // Recursively check sub_child_overlay
    if (Array.isArray(node.sub_child_overlay)) {
        for (const subChild of node.sub_child_overlay) {
            count += getCountOfChangedRanges(subChild);
        }
    }

    return count;
}

// Recursive comparison for a single filter node
export const isAnyRangeChanged = (filter: any, prevFilter: any): boolean => {
    if (!filter || !prevFilter) return false;

    if (
        filter.rangeMin !== prevFilter.rangeMin ||
        filter.rangeMax !== prevFilter.rangeMax
    ) {
        return true;
    }

    // Check child_overlay arrays
    if (Array.isArray(filter.child_overlay) && Array.isArray(prevFilter.child_overlay)) {
        for (let i = 0; i < filter.child_overlay.length; i++) {
            if (isAnyRangeChanged(filter.child_overlay[i], prevFilter.child_overlay[i])) {
                return true;
            }
        }
    }

    // Check sub_child_overlay arrays
    if (Array.isArray(filter.sub_child_overlay) && Array.isArray(prevFilter.sub_child_overlay)) {
        for (let i = 0; i < filter.sub_child_overlay.length; i++) {
            if (isAnyRangeChanged(filter.sub_child_overlay[i], prevFilter.sub_child_overlay[i])) {
                return true;
            }
        }
    }

    return false;
};

// Compare arrays of filters
export const isAnyRangeChangedInFilters = (filters: any[], prevFilters: any[]): boolean => {
    if (!Array.isArray(filters) || !Array.isArray(prevFilters)) return false;

    for (let i = 0; i < filters.length; i++) {
        const filter = filters[i];
        const prevFilter = prevFilters.find((f: any) => f.key === filter.key);
        if (prevFilter && isAnyRangeChanged(filter, prevFilter)) {
            return true;
        }
    }
    return false;
};

export const countOfChangedSlider = (filters: any[], overlays: any[], config: any[]): number => {
    if (!Array.isArray(filters) || !Array.isArray(overlays)) return 0;

    let total = 0;
    const filteredOverlays = overlays.filter(item => !item.isdisable) || [];

    for (const filter of filters) {
        if (filteredOverlays.find(overlay => overlay.key === filter.key)) {
            total += getCountOfChangedRanges(filter);
        }
    }
    return total;
}
export const convertCurrency = (amount: number, selCurrency: string): number => {
    const USD_TO_INR = 83.7;
    if (selCurrency === 'USD') {
        return Math.floor(amount / USD_TO_INR);
    } else if (selCurrency === 'INR') {
        return Math.floor(amount * USD_TO_INR);
    }
    return amount;
}

type OverlayNode = {
    key: string;
    min?: number;
    max?: number;
    rangeMin?: number;
    rangeMax?: number;
    child_overlay?: OverlayNode[];
    sub_child_overlay?: OverlayNode[];
    [key: string]: any;
};

export const indexToLetter = (index: number) => {
    if (index < 26) {
        return String.fromCharCode(97 + index); // 'a' is 97 in ASCII
    } else {
        // For indices beyond 26, you can use a fallback or extend with aa, ab, etc.
        return index + 1; // or handle as you prefer
    }
};

export const updateOverlayValues = (
    overlays: OverlayNode[],
    targetKey: string,
    selCurrency: string
): OverlayNode[] => {
    return overlays.map((node) => {
        if (node.key === targetKey) {
            // Update this node and all descendants using convertCurrency
            const updateNode = (n: OverlayNode): OverlayNode => {
                const updated: OverlayNode = {
                    ...n,
                    min: n.min !== undefined ? convertCurrency(n.min, selCurrency) : n.min,
                    max: n.max !== undefined ? convertCurrency(n.max, selCurrency) : n.max,
                    rangeMin: n.rangeMin !== undefined ? convertCurrency(n.rangeMin, selCurrency) : n.rangeMin,
                    rangeMax: n.rangeMax !== undefined ? convertCurrency(n.rangeMax, selCurrency) : n.rangeMax,
                };
                if (Array.isArray(n.child_overlay)) {
                    updated.child_overlay = n.child_overlay.map(updateNode);
                }
                if (Array.isArray(n.sub_child_overlay)) {
                    updated.sub_child_overlay = n.sub_child_overlay.map(updateNode);
                }
                return updated;
            };
            return updateNode(node);
        } else return node;
    });
};

export const calculateSmartStep = (min: number, max: number, targetSteps = 150): number => {
    if (max < 1000) return 1;
    const range = max - min;
    const rawStep = range / targetSteps;

    if (rawStep <= 0) return 1;

    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalized = rawStep / magnitude;

    let roundedNormalized;

    if (normalized < 1.5) {
        roundedNormalized = 1;
    } else if (normalized < 3.5) {
        roundedNormalized = 2.5;
    } else if (normalized < 7.5) {
        roundedNormalized = 5;
    } else {
        roundedNormalized = 10;
    }

    return roundedNormalized * magnitude;
}

export const getSafeSliderRange = (
    min: number,
    max: number,
    rangeMin: number,
    rangeMax: number,
) => {

    let safeMin = rangeMin;
    let safeMax = rangeMax;
    let adjusted = false;

    if (rangeMin > max || rangeMax < min) {
        safeMin = min;
        safeMax = max;
        adjusted = true;
    }

    if (safeMin !== Math.max(min, safeMin)) {
        safeMin = Math.max(min, safeMin);
        adjusted = true;
    }
    if (safeMax !== Math.min(max, safeMax)) {
        safeMax = Math.min(max, safeMax);
        adjusted = true;

    }

    if (safeMin > safeMax) {
        safeMin = safeMax = min;
        adjusted = true;

    }


    return { min: safeMin, max: safeMax, isadjusted: adjusted };
}

export const getSnappedSliderRange = (min: number, max: number, newMin: number, newMax: number): { min: number, max: number } => {
    let snappedMin = newMin;
    let snappedMax = newMax;

    const step = calculateSmartStep(min, max);

    if (max > 1000) {
        // Snap max if close to max
        if (Math.abs(newMax - max) < step) {
            snappedMax = max;
        }

        // Snap min if close to min
        if (Math.abs(newMin - min) < step) {
            snappedMin = min;
        }

        // Also snap min to max if nearly equal and user intends to max-out
        if (Math.abs(newMin - max) < step) {
            snappedMin = max;
        }

        // Also snap max to min if nearly equal and user intends to min-out
        if (Math.abs(newMax - min) < step) {
            snappedMax = min;
        }
    }
    return { min: snappedMin, max: snappedMax };
}

export const extractAllFiltersWithSafeRange = (newSliders: any[], filters: any[]) => {
    return newSliders.map(slider => {
        const matchingFilter = filters.find(f => f.key === slider.key);
        return matchingFilter
            ? extractFiltersWithSafeRange(slider, matchingFilter)
            : structuredClone(slider); // Ensure no shared reference
    });
};


export const extractFiltersWithSafeRange = (dataTree: any, filterTree: any): any => {
    if (!dataTree || !filterTree) return structuredClone(dataTree);

    const hasSafeRange = filterTree.rangeMin !== undefined && filterTree.rangeMax !== undefined;

    const updatedNode: any = {
        ...dataTree,
        ...(hasSafeRange && {
            rangeMin: getSafeSliderRange(dataTree.min, dataTree.max, filterTree.rangeMin, filterTree.rangeMax).min,
            rangeMax: getSafeSliderRange(dataTree.min, dataTree.max, filterTree.rangeMin, filterTree.rangeMax).max,
            isadjusted: getSafeSliderRange(dataTree.min, dataTree.max, filterTree.rangeMin, filterTree.rangeMax).isadjusted
        })
    };

    if (Array.isArray(dataTree.child_overlay) && Array.isArray(filterTree.child_overlay)) {
        updatedNode.child_overlay = dataTree.child_overlay.map((child: any, i: number) =>
            extractFiltersWithSafeRange(child, filterTree.child_overlay[i])
        );
    }

    if (Array.isArray(dataTree.sub_child_overlay) && Array.isArray(filterTree.sub_child_overlay)) {
        updatedNode.sub_child_overlay = dataTree.sub_child_overlay.map((child: any, i: number) =>
            extractFiltersWithSafeRange(child, filterTree.sub_child_overlay[i])
        );
    }

    return updatedNode;
};


// Recursively update rangeMin and rangeMax from filterTree to dataTree by matching keys
export const syncRangesFromFilter = (dataTree: any, filterTree: any) => {
    if (!dataTree || !filterTree) return dataTree;

    if (dataTree.key === filterTree.key) {
        const rangeMin = filterTree.rangeMin ?? dataTree.min;
        const rangeMax = filterTree.rangeMax ?? dataTree.max;

        const { min, max } = getSafeSliderRange(dataTree.min, dataTree.max, rangeMin, rangeMax);

        dataTree.rangeMin = min;
        dataTree.rangeMax = max;
    }

    if (Array.isArray(dataTree.child_overlay) && Array.isArray(filterTree.child_overlay)) {
        dataTree.child_overlay = dataTree.child_overlay.map((child: any, i: number) =>
            syncRangesFromFilter(child, filterTree.child_overlay[i])
        );
    }
    if (Array.isArray(dataTree.sub_child_overlay) && Array.isArray(filterTree.sub_child_overlay)) {
        dataTree.sub_child_overlay = dataTree.sub_child_overlay.map((child: any, i: number) =>
            syncRangesFromFilter(child, filterTree.sub_child_overlay[i])
        );
    }
    return dataTree;
}

export const addRangeMinMax = (obj: any) => {
    if (typeof obj.min === 'number' && typeof obj.max === 'number') {
        obj.rangeMin = obj.rangeMin ?? obj.min;
        obj.rangeMax = obj.rangeMax ?? obj.max;
    }

    // Process child overlays first
    if (Array.isArray(obj.child_overlay)) {
        obj.child_overlay = obj.child_overlay.map((child: any) => {
            // Add rangeMin/rangeMax to child
            let updatedChild = addRangeMinMax(child);

            // Then process its sub_child_overlay
            if (Array.isArray(updatedChild.sub_child_overlay)) {
                updatedChild.sub_child_overlay = updatedChild.sub_child_overlay.map(addRangeMinMax);
            }

            return updatedChild;
        });
    }

    return obj;
}

export const compressNumber = (
    value: number,
    curr = 'INR',
    legend = false,
    roundUp = true,
    compress = true,
    showSymbol = false
) => {
    if (value === null || value === undefined || isNaN(value)) return value;

    let notation = '';
    let convertedValue = Number(value);
    let symbol = '';

    const roundValue = (val: number) => {
        if (val < 10) return Number(val.toFixed(2));
        else if (val < 100) return Number(val.toFixed(1));
        else return Math.round(val);
    };

    const formatNumber = (num: number, locale: string) => {
        return num.toLocaleString(locale);
    };

    if (curr === 'USD' || curr === 'US') {
        symbol = showSymbol ? '$' : '';
        if (compress) {
            if (value >= 1e12) {
                notation = legend ? ' Tn' : ' Tn';
                convertedValue = value / 1e12;
            } else if (value >= 1e9) {
                notation = legend ? ' Bn' : ' Bn';
                convertedValue = value / 1e9;
            } else if (value >= 1e6) {
                notation = legend ? ' Mn' : ' Mn';
                convertedValue = value / 1e6;
            } else if (value >= 1e3) {
                notation = legend ? ' K' : '';
                convertedValue = legend ? value / 1e3 : value;
            }
        }
        convertedValue = roundValue(convertedValue);
        let formatted = formatNumber(convertedValue, 'en-US');
        return symbol + formatted.replace(/\.00$/, '') + notation;

    } else if (curr === 'INR' || curr === 'IN') {
        symbol = showSymbol ? '₹' : '';
        if (compress) {
            if (value >= 1e7) {
                notation = legend ? ' Cr' : ' Cr';
                convertedValue = value / 1e7;
            } else if (value >= 1e5) {
                notation = legend ? ' L' : ' L';
                convertedValue = value / 1e5;
            } else if (value >= 1e3) {
                notation = legend ? ' K' : '';
                convertedValue = legend ? value / 1e3 : value;
            }
        }
        convertedValue = roundValue(convertedValue);
        let formatted = formatNumber(convertedValue, 'en-IN');
        return symbol + formatted.replace(/\.00$/, '') + notation;
    }

    return value.toString(); // fallback
}

export const updateRangeByKey = (obj: any, targetKey: string, newRangeMin: number, newRangeMax: number) => {
    if (!obj) return obj;
    if (obj.key === targetKey) {
        return { ...obj, rangeMin: newRangeMin, rangeMax: newRangeMax };
    }
    if (Array.isArray(obj.child_overlay)) {
        return {
            ...obj,
            child_overlay: obj.child_overlay.map((child: any) =>
                updateRangeByKey(child, targetKey, newRangeMin, newRangeMax)
            )
        };
    }
    if (Array.isArray(obj.sub_child_overlay)) {
        return {
            ...obj,
            sub_child_overlay: obj.sub_child_overlay.map((child: any) =>
                updateRangeByKey(child, targetKey, newRangeMin, newRangeMax)
            )
        };
    }
    return obj;
}

export const updateRangeIfChanged = (obj: any, targetKey: string, newRangeMin: number, newRangeMax: number): any => {
    if (!obj) return obj;

    // Check if range should be added/updated
    let updated = { ...obj };
    if ((obj.key === targetKey) && typeof obj.min === 'number' && typeof obj.max === 'number' && (obj.min !== newRangeMin || obj.max !== newRangeMax)) {
        updated.rangeMin = newRangeMin;
        updated.rangeMax = newRangeMax;
    } else {
        // Remove rangeMin/rangeMax if present and not needed
        if ('rangeMin' in updated) delete updated.rangeMin;
        if ('rangeMax' in updated) delete updated.rangeMax;
    }

    // Recursively process child_overlay
    if (Array.isArray(obj.child_overlay)) {
        updated.child_overlay = obj.child_overlay.map((child: any) =>
            updateRangeIfChanged(child, targetKey, newRangeMin, newRangeMax)
        );
    }
    // Recursively process sub_child_overlay
    if (Array.isArray(obj.sub_child_overlay)) {
        updated.sub_child_overlay = obj.sub_child_overlay.map((child: any) =>
            updateRangeIfChanged(child, targetKey, newRangeMin, newRangeMax)
        );
    }

    return updated;
};

// Helper: Upsert (replace or add) overlay tree in filters
export const upsertFilterTree = (filters: any[], newTree: any) => {
    const idx = filters.findIndex(f => f.key === newTree.key);
    if (idx === -1) return [...filters, newTree];
    const updated = [...filters];
    updated[idx] = newTree;
    return updated;
}

export const resetRangesToMinMax = (obj: any): any => {
    if (!obj) return obj;
    let updated = { ...obj };
    if (typeof updated.min === 'number' && typeof updated.max === 'number') {
        updated.rangeMin = updated.min;
        updated.rangeMax = updated.max;
    }
    // Reset children (if any)
    if (Array.isArray(updated.child_overlay)) {
        updated.child_overlay = updated.child_overlay.map((child: any) => {
            let childUpdated = resetRangesToMinMax(child);
            // Only child_overlay items can have sub_child_overlay
            if (Array.isArray(childUpdated.sub_child_overlay)) {
                childUpdated.sub_child_overlay = childUpdated.sub_child_overlay.map(resetRangesToMinMax);
            }
            return childUpdated;
        });
    }
    return updated;
}

export const removeAllRangeMinMax = (obj: any): any => {
    if (!obj) return obj;
    // Create a shallow copy to avoid mutating the original
    const updated = { ...obj };
    if ('rangeMin' in updated) delete updated.rangeMin;
    if ('rangeMax' in updated) delete updated.rangeMax;

    if (Array.isArray(updated.child_overlay)) {
        updated.child_overlay = updated.child_overlay.map(removeAllRangeMinMax);
    }
    if (Array.isArray(updated.sub_child_overlay)) {
        updated.sub_child_overlay = updated.sub_child_overlay.map(removeAllRangeMinMax);
    }
    return updated;
};

export const isAddedToCompare = (geoId: any, compareList: { districts: any[], subdistricts: any[], pincodes: any[], villages: any[], Branch: any[], Partner:any[], RelationshipManager: any[] }, pType: string) => {
    const districts = compareList?.districts ?? [];
    const subdistricts = compareList?.subdistricts ?? [];
    const pincodes = compareList?.pincodes ?? [];
    const villages = compareList?.villages ?? [];
    const catchments = (pType === 'Branch'? compareList?.Branch :pType  === 'Partner'? compareList?.Partner :compareList?.RelationshipManager) ?? [];

    const found = [...districts, ...subdistricts, ...pincodes, ...villages,...catchments].find((geo: any) => geo.geoId == geoId);
    return !!found;
}

export const countLeafGeoIds = (geoTree: any[], idSet: Set<number>, type: number, seen: Set<number> = new Set()): number => {
    let count = 0;

    if (!Array.isArray(geoTree) || geoTree?.length === 0) return count;

    for (const node of geoTree) {
        if (Array.isArray(node.sub_regions) && node.sub_regions.length > 0) {
            if (type === 3 && idSet.has(node.geo_id) && !seen.has(node.geo_id)) {
                seen.add(node.geo_id);
                count += 1;
            } else {
                count += countLeafGeoIds(node.sub_regions, idSet, type, seen);
            }
        } else {
            if (type !== 3 && idSet.has(node.geo_id) && !seen.has(node.geo_id)) {
                seen.add(node.geo_id);
                count += 1;
            }
        }
    }
    return count;
};

export const isAddToCompareChanged = (tempIds: Set<number>, compareIds: Set<number>) => {
    if (tempIds.size !== compareIds.size) return true;

    for (let id of tempIds) {
        if (!compareIds.has(id)) return true;
    }
    return false;
};

export const hasAdjusted = (filters: any[]): boolean => {
    for (const filter of filters) {
        if (filter.isadjusted) return true;
        if (Array.isArray(filter.child_overlay) && hasAdjusted(filter.child_overlay)) return true;
        if (Array.isArray(filter.sub_child_overlay) && hasAdjusted(filter.sub_child_overlay)) return true;
    }
    return false;
};
export const getKpiBgColor = (type: string, isSelected: boolean) => {
    if (isSelected) {
        return 'bg-white';
    } else {
        if (type === 'cTVData') {
            return 'bg-ctv';
        } else if (type === 'poiData') {
            return 'bg-poi';
        } else if (type === 'EhData') {
            return 'bg-eh';
        }
        return 'bg-white';
    }
}


const isDevEnv = () => {
    if (typeof window !== "undefined") {
        return window.location.protocol === "http:";
    }
};

export const logger = {
    log: (...args: any) => {
        if (isDevEnv()) {
            console.log(...args);
        }
    },
    warn: (...args: any) => {
        if (isDevEnv()) {
            console.warn(...args);
        }
    },
    error: (...args: any) => {
        if (isDevEnv()) {
            console.error(...args);
        }
    }
};

export const processGeometry = (geometry: google.maps.Data.Geometry): google.maps.LatLngLiteral[][] => {
    const result: google.maps.LatLngLiteral[][] = [];

    const process = (geom: any) => {
        if (geom.getType() === 'Polygon') {
            const polyCoords = geom.getArray()[0].getArray().map((coord: any) => ({
                lat: coord.lat(),
                lng: coord.lng()
            }));
            result.push(polyCoords);
        } else if (geom.getType() === 'MultiPolygon') {
            geom.getArray().forEach((polygon: any) => process(polygon));
        }
    };

    process(geometry);
    return result;
};

// Process GeoJSON geometry (plain object) into LatLngLiteral arrays
export const processGeoJSONGeometry = (geometries: any[]): google.maps.LatLngLiteral[][] => {
    const result: google.maps.LatLngLiteral[][] = [];

    geometries?.forEach((geometry) => {
        if (!geometry || !geometry.type || !geometry.coordinates) return;

        if (geometry.type === 'Polygon') {
            // Polygon: coordinates[0] is the outer ring
            const coords = geometry.coordinates[0].map((coord: number[]) => ({
                lng: coord[0],
                lat: coord[1]
            }));
            result.push(coords);
        } else if (geometry.type === 'MultiPolygon') {
            // MultiPolygon: iterate through each polygon
            geometry.coordinates.forEach((polygon: number[][][]) => {
                const coords = polygon[0].map((coord: number[]) => ({
                    lng: coord[0],
                    lat: coord[1]
                }));
                result.push(coords);
            });
        }
    });

    return result;
};

export const subGeoLevel = (selected: any, selectedSubLevel: number, catchment: any) => {
    const { country, state, district, sub_district,village } = selected;
    const geo_id = village || sub_district || district || state || country;
    
    if(Number(geo_id) == village && !catchment) return "hex"
    if (geo_id === sub_district || catchment) return 'village';

    if (geo_id === district) {
        if (selectedSubLevel === 4) return 'sub_district';
        else return 'pincode';
    }
    else if (geo_id === state) return 'district';
    else if (geo_id === country) return 'state';
    return '';
}

export const getRoIBounds = (map: google.maps.Map, rect: { HEIGHT: number, WIDTH: number }) => {
    const bounds = map.getBounds();
    const ne = bounds?.getNorthEast();
    const sw = bounds?.getSouthWest();

    const minLat = sw?.lat(); // South
    const minLng = sw?.lng(); // West
    const maxLat = ne?.lat(); // North
    const maxLng = ne?.lng(); // East

    if (typeof minLat === "number" && typeof maxLat === "number" && typeof minLng === "number" && typeof maxLng === "number") {
        let centerLat = (minLat + maxLat) / 2;
        let centerLng = (minLng + maxLng) / 2;

        const latSpan = maxLat - minLat;
        const lngSpan = maxLng - minLng;

        const rectLatSpan = latSpan * rect.HEIGHT;
        const rectLngSpan = lngSpan * rect.WIDTH;

        const rectMinLat = centerLat - rectLatSpan / 2;
        const rectMaxLat = centerLat + rectLatSpan / 2;
        const rectMinLng = centerLng - rectLngSpan / 2;
        const rectMaxLng = centerLng + rectLngSpan / 2;

        /*
         <----------------REMOVE THE BELOW RECTANGLE AFTER TESTING/DEBUGGING----------------->
         <-------THIS RECTANGLE IS JUST FOR DEBUGGING PURPOSES TO VISUALIZE THE BOUNDS------->
        */
        // if (rect.HEIGHT === 0.65) {
        //     const rectangle = new window.google.maps.Rectangle({
        //         bounds: {
        //             north: rectMaxLat,
        //             south: rectMinLat,
        //             east: rectMaxLng,
        //             west: rectMinLng
        //         },
        //         editable: false,
        //         draggable: false,
        //         map: map,
        //         strokeColor: "#FF0000",
        //         strokeWeight: 2,
        //         fillOpacity: 0,
        //         zIndex: 1
        //     });
        // }

        return {
            north: rectMaxLat,
            south: rectMinLat,
            east: rectMaxLng,
            west: rectMinLng
        }
    }
    return null;
}

export const getRoIBoundsUsingPosition = (map: google.maps.Map, rect: { HEIGHT: number, WIDTH: number }) => {
    const mapContainer = map.getDiv();

    // Get current map container dimensions
    const mapWidth = mapContainer.offsetWidth;
    const mapHeight = mapContainer.offsetHeight;

    // Calculate the virtual ROI (Region of Interest)
    const roiWidth = mapWidth * rect.WIDTH;
    const roiHeight = mapHeight * rect.HEIGHT;

    // Calculate starting and ending pixel coordinates of the ROI
    const roiXStart = (mapWidth - roiWidth) / 2;
    const roiYStart = (mapHeight - roiHeight) / 2;
    const roiXEnd = roiXStart + roiWidth;
    const roiYEnd = roiYStart + roiHeight;

    /*
   * <----------------REMOVE THE BELOW RECTANGLE AFTER TESTING/DEBUGGING----------------->
   * <---This debug rectangle can be rendered using CSS overlay for visualization.--->
   */
    // const debugRect = document.createElement("div");
    // debugRect.style.position = "absolute";
    // debugRect.style.left = `${roiXStart}px`;
    // debugRect.style.top = `${roiYStart}px`;
    // debugRect.style.width = `${roiWidth}px`;
    // debugRect.style.height = `${roiHeight}px`;
    // debugRect.style.border = "2px dashed red";
    // debugRect.style.pointerEvents = "none";
    // debugRect.id = "roi-debug-rect";
    // mapContainer.appendChild(debugRect);

    return {
        left: roiXStart,
        top: roiYStart,
        right: roiXEnd,
        bottom: roiYEnd,
        width: roiWidth,
        height: roiHeight,
    };
}

export const checkOutOfBoundsUsingPosition = (
    position: { x: number; y: number },
    roiBounds: {
        left: number;
        top: number;
        right: number;
        bottom: number;
        width: number;
        height: number;
    } | null
) => {
    const outOfBoundsSides: string[] = [];
    if (!roiBounds) return outOfBoundsSides;

    const { x, y } = position;
    const { left, top, right, bottom } = roiBounds;

    // Check vertical boundaries
    if (y < top) outOfBoundsSides.push("top");
    else if (y > bottom) outOfBoundsSides.push("bottom");

    // Check horizontal boundaries
    if (x < left) outOfBoundsSides.push("left");
    else if (x > right) outOfBoundsSides.push("right");

    return outOfBoundsSides;
};


export const checkOutOfBounds = (latLng: { lat: number; lng: number }, bounds: { north: number; south: number; east: number; west: number } | null) => {
    const outOfBoundsSides: string[] = [];

    if (!bounds) return outOfBoundsSides;

    // Check if latitude is above the northern boundary (TOP)
    if (latLng.lat > bounds.north) {
        outOfBoundsSides.push("top");
    }
    // Check if latitude is below the southern boundary (BOTTOM)
    else if (latLng.lat < bounds.south) {
        outOfBoundsSides.push("bottom");
    }

    // Check if longitude is beyond the eastern boundary (RIGHT)
    if (latLng.lng > bounds.east) {
        outOfBoundsSides.push("right");
    }
    // Check if longitude is beyond the western boundary (LEFT)
    else if (latLng.lng < bounds.west) {
        outOfBoundsSides.push("left");
    }
    console.log('Out of bounds sides:', outOfBoundsSides);
    return outOfBoundsSides;
};

// Check if screen coordinates are out of viewport bounds
export const checkOutOfBoundsScreen = (
    screenCoords: { x: number; y: number },
    viewportBounds: { width: number; height: number; offsetX?: number; offsetY?: number } | null,
    cardDimensions?: { width: number; height: number }
) => {
    const outOfBoundsSides: string[] = [];

    if (!viewportBounds) return outOfBoundsSides;

    const offsetX = viewportBounds.offsetX || 0;
    const offsetY = viewportBounds.offsetY || 0;
    const cardWidth = cardDimensions?.width || 200; // Default card width
    const cardHeight = cardDimensions?.height || 100; // Default card height

    // Check if card would go beyond RIGHT edge
    if (screenCoords.x + cardWidth > viewportBounds.width + offsetX) {
        outOfBoundsSides.push("right");
    }
    // Check if card would go beyond LEFT edge
    if (screenCoords.x < offsetX) {
        outOfBoundsSides.push("left");
    }

    // Check if card would go beyond BOTTOM edge
    if (screenCoords.y + cardHeight > viewportBounds.height + offsetY) {
        outOfBoundsSides.push("bottom");
    }
    // Check if card would go beyond TOP edge
    if (screenCoords.y < offsetY) {
        outOfBoundsSides.push("top");
    }

    return outOfBoundsSides; // <--- this will give you the class names to adjust the position
};

const isValidUrl = (str: string): boolean => {
    if (!str) return false;

    const pattern = /^(https?:\/\/|www\.)[^\s/$.?#].[^\s]*$/i;
    return pattern.test(str.trim());
};

export const renderCellValue = (val: unknown, key: string, row?: any, fn?: Function): React.ReactNode => {
    if (!val) return "NA";

    if (Array.isArray(val)) {
        return val.length > 0
            ? React.createElement(
                "span",
                null,
                val.map((v) => renderCellValue(v, key, row)).join(", ")
            )
            : "NA";
    }
    else if (key && key.toLowerCase().includes("date") && val !== 'NA') {
        return convertEpochToLocalDate(val as number);
    } else if (typeof val === "string" && isValidUrl(val)) {
        return React.createElement(LinkWithIcon, { url: val });
    } else if (key.includes(".") && row) {
        const parentKey = key.split(".")[0];
        const parentObj = row[parentKey];

        if (parentObj && typeof parentObj === "object" && "icon" in parentObj) {
            const isHighlighted = fn && fn(row.branchId) && row.type === 'poi';

            return React.createElement(
                "span",
                { style: { display: "inline-flex", alignItems: "center", gap: "5px" } },
                [
                    React.createElement("img", {
                        key: "icon",
                        src: isHighlighted ? highlightedPoI : parentObj.icon,
                        alt: "icon",
                        style: { width: 10, height: 10, boxSizing: "border-box", aspectRaio: '1/1' }
                    }),
                    React.createElement("span", { key: "text" }, val as string)
                ]
            );
        } else return val as React.ReactNode;
    }
    return val as React.ReactNode;
};

export const buildColumns = (tableHeaderData: any[], fn?: Function): ColumnDef<any>[] => {
    return tableHeaderData?.map((header) => {

        return {
            ...header,
            id: header.accessorKey,
            cell: (info: any) => {
                return renderCellValue(info.getValue(), header.accessorKey, info.row.original, fn)
            },
        };
    });
};

export const convertEpochToLocalDate = (epoch: number | string): string => {
    let epochNum;
    if (!epoch) epochNum = 0;
    else epochNum = typeof epoch === "string" ? parseInt(epoch, 10) : epoch;
    if (isNaN(epochNum)) epochNum = 0;

    const date = new Date(epochNum * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

export const getParentGeoId = (geoId: number, selected: any, selTab: number) => {
    const { country, state, district, sub_district, village } = selected;
    if (geoId == village) return { parent: Number(sub_district), type: 'village' };
    else if (geoId == sub_district) {
        if (selTab === 4) return { parent: Number(district), type: 'sub_district' };
        else if (selTab === 5) return { parent: Number(district), type: 'pincode' };
    }
    else if (geoId == district) return { parent: Number(state), type: 'district' };
    else if (geoId == state) return { parent: Number(country), type: 'state' };;
    return { parent: null, type: 'country' };;
}

export const getAdjacencyData = () => {
    const data = localStorage.getItem('adjacency');
    return data ? JSON.parse(data) : null;
};

export const applyAdjacencyMiddleware = (config: any) => {
    const adjacency = getAdjacencyData();

    if (adjacency) {
        if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
            config.data = {
                ...config.data,
                adjacency,
            };
        } else {
            config.params = {
                ...config.params,
                adjacency,
            };
        }
    }
    return config;
};

export const cleanFilterTree = (filter: any): any => {
    // First, clean the current filter node
    let cleanedFilter = { ...filter };

    if (
        cleanedFilter.rangeMin !== undefined &&
        cleanedFilter.rangeMax !== undefined &&
        cleanedFilter.min !== undefined &&
        cleanedFilter.max !== undefined &&
        cleanedFilter.rangeMin === cleanedFilter.min &&
        cleanedFilter.rangeMax === cleanedFilter.max
    ) {
        const { rangeMin, rangeMax, ...rest } = cleanedFilter;
        cleanedFilter = rest;
    }

    // Then, recurse into children if present
    if (Array.isArray(cleanedFilter.child)) {
        cleanedFilter.child = cleanedFilter.child.map(cleanFilterTree);
    }

    if (Array.isArray(cleanedFilter.sub_child)) {
        cleanedFilter.sub_child = cleanedFilter.sub_child.map(cleanFilterTree);
    }

    if (Array.isArray(cleanedFilter.child_overlay)) {
        cleanedFilter.child_overlay = cleanedFilter.child_overlay.map(cleanFilterTree);
    }

    if (Array.isArray(cleanedFilter.sub_child_overlay)) {
        cleanedFilter.sub_child_overlay = cleanedFilter.sub_child_overlay.map(cleanFilterTree);
    }
    return cleanedFilter;
};

export const getCurrGeoLevelName = (selectedGeo: any) => {
    const { country, state, district, sub_district, village } = selectedGeo;

    if (village) return 'village';
    if (sub_district) return 'sub_district';
    if (district) return 'district';
    if (state) return 'state';
    if (country) return 'country';
    return null;
}
export const removeParentFeature = (geoJSON: any, selectedGeo: any, selectedTab: number, catchment: boolean) => {
    if (!geoJSON || !selectedGeo || (!catchment && selectedGeo.village)) return geoJSON;

    const selectedGeoId = selectedGeo.sub_district || selectedGeo.district || selectedGeo.state || selectedGeo.country;
    const getCurrLevel = getCurrGeoLevelName(selectedGeo);

    const filteredFeatures = geoJSON.features.filter((feature: any) => {
        // Handle both old structure (geoLevel) and new structure (geoValue)
        const geoLevel = feature.properties.geoValue || feature.properties.geoLevel;

        // Global check: always exclude the feature if its ID matches the selected geo ID
        if (catchment && geoLevel === 'catchment') return false;

        if (!catchment) {
            if (getCurrLevel === geoLevel) return false;
            if (getCurrLevel === "sub_district" && geoLevel == "pincode") return false;

            // If at district level, show appropriate child features based on tab
            if (selectedGeoId === selectedGeo.district && !catchment) {
                if (geoLevel === 'district' || geoLevel === 'catchment') return true;
                return (selectedTab === 4 && geoLevel === 'sub_district') || (selectedTab === 5 && geoLevel === 'pincode');
            }
        }

        // For other levels, keep all features
        return true;
    });

    return {
        ...geoJSON,
        features: filteredFeatures
    };
};

export const getCurrLevel = (level: number) => {
    switch (level) {
        case 1:
            return 'country';
        case 2:
            return 'state';
        case 3:
            return 'district';
        case 4:
            return 'sub_district';
        case 5:
            return 'sub_district';
        case 6:
            return 'village';
        default:
            return null;
    }
}

export const getPathData = (geoJSON: any, selectedGeo: any, catchment: string | null) => {
    const currGeoLevel = getCurrGeoLevelName(selectedGeo);
    return geoJSON.features
        .filter((feature: any) => {
            // Handle both old structure (geoLevel) and new structure (geoValue)
            const geoLevel = feature.properties?.geoValue || feature.properties?.geoLevel;
            // Exclude hexagons from path data - H3HexagonLayer handles its own borders
            if (geoLevel === 'hex') return false;
            return (catchment && geoLevel === 'catchment') || (geoLevel === currGeoLevel || (currGeoLevel === "sub_district" && geoLevel == "pincode"));
        })
        .flatMap((feature: any) => {
            const geometry = feature.geometry;
            const properties = feature.properties;

            if (!geometry || !geometry.coordinates) return [];

            if (geometry.type === 'Polygon') {
                // Polygon: coordinates[0] is the outer ring
                return [{
                    coordinates: geometry.coordinates[0],
                    properties,
                }];
            } else if (geometry.type === 'MultiPolygon') {
                // MultiPolygon: each polygon's first coordinate ring
                return geometry.coordinates.map((polygon: number[][][]) => ({
                    coordinates: polygon[0],
                    properties,
                }));
            }
            return [];
        });
}

export const getLayerInfoText = (layer: string, disabled: boolean, isToggled: boolean, isChecked: any, geoCode: number, selectedGeo: any, selCatchmentType: any, selMetric: any, selSubRegion: any, catchment: number | null) => {
    // Handle subregion boundaries layer
    if (layer === 'subregionBoundaries') {
        if (isChecked?.catchments && disabled) {
            return "Disabled due to Catchments";
        }
        return `Showing: ${selSubRegion?.title}`;
    }

    // Handle catchments layer
    if (layer === 'catchments' && isToggled && !disabled) {
        return `Showing: ${selCatchmentType?.title || ""}`;
    }

    // Handle EH layer when disabled at sub_district level
    if (layer === 'EH' && disabled && !isToggled) {
        if (catchment)
            return "Turn on Village Boundaries to enable this layer.";
        else if (geoCode === Number(selectedGeo.sub_district))
            return "Turn on Village Boundaries or Catchments to enable this layer.";
    }

    // Handle metric display for data layers (EH, CTV, PoI)
    if ((layer === 'EH' || layer === 'CTV' || layer === 'PoI') && isToggled) {
        return `Metric: ${selMetric?.[layer as MetricKey]?.title}`;
    }

    return undefined;
}

export const formatImageDate = (dateString: string): string => {
    // Parse date string like "2023-06" or "2023"
    const parts = dateString.split('-');
    const year = parts[0];
    const month = parts[1];

    if (month) {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthIndex = parseInt(month, 10) - 1;
        return `Captured on: ${monthNames[monthIndex]} ${year}`;
    }

    return `Captured on: ${year}`;
};

export const normalizeLatLng = (latLng: google.maps.LatLng | undefined) =>
    latLng ? { lat: latLng.lat(), lng: latLng.lng() } : { lat: 20.5937, lng: 78.9629 };

export const setupZoomControl = (shouldDisable: boolean = false) => {
    const zoomOutButton: any = document.querySelector('button[title="Zoom out"]');

    if (zoomOutButton) {
        zoomOutButton.disabled = shouldDisable;
        zoomOutButton.style.opacity = shouldDisable ? "0.5" : "1";
        zoomOutButton.style.cursor = shouldDisable ? "default" : "pointer";
    }
};

/**
 * Generic function to detect markers that are colliding based on screen position and map zoom level
 * Works for both MBPs and POIs
 * @param clickedMarker - The marker that was clicked
 * @param allMarkers - Array of all markers to check for collisions
 * @param map - Google Maps instance to get projection and zoom level
 * @param collisionRadius - Collision radius in pixels (27.17 for MBPs, 15 for POIs)
 * @param uniqueKeyField - Field name used as unique identifier ('unique_key' for MBPs, 'branchId' for POIs)
 * @returns Array of markers that are colliding with the clicked marker, sorted by distance (nearest first)
 */
export const getCollidingMarkers = (
    clickedMarker: any,
    allMarkers: any[],
    map: google.maps.Map | null,
    collisionRadius: number,
    uniqueKeyField: string = 'unique_key'
): any[] => {
    if (!clickedMarker || !allMarkers || !map) return [];

    const projection = map.getProjection();
    if (!projection) return [];

    // Convert clicked marker lat/lng to pixel coordinates
    const clickedLatLng = new google.maps.LatLng(clickedMarker.latitude, clickedMarker.longitude);
    const clickedPoint = projection.fromLatLngToPoint(clickedLatLng);

    if (!clickedPoint) return [];

    // Get map zoom level to calculate proper scale
    const zoom = map.getZoom() || 1;
    const scale = Math.pow(2, zoom);

    // Convert world coordinates to pixel coordinates
    const clickedPixel = {
        x: clickedPoint.x * scale,
        y: clickedPoint.y * scale
    };

    const collidingMarkers: Array<{ marker: any; distance: number }> = [];

    // Check each marker for collision
    allMarkers.forEach(marker => {
        // Skip the clicked marker itself
        if (marker[uniqueKeyField] === clickedMarker[uniqueKeyField]) return;

        // Convert marker lat/lng to pixel coordinates
        const markerLatLng = new google.maps.LatLng(marker.latitude, marker.longitude);
        const markerPoint = projection.fromLatLngToPoint(markerLatLng);

        if (!markerPoint) return;

        const markerPixel = {
            x: markerPoint.x * scale,
            y: markerPoint.y * scale
        };

        // Calculate distance between markers in pixels
        const dx = clickedPixel.x - markerPixel.x;
        const dy = clickedPixel.y - markerPixel.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Check if markers overlap (distance less than sum of their radii)
        const totalRadius = collisionRadius * 2; // Both markers have the same radius

        if (distance < totalRadius) {
            collidingMarkers.push({ marker, distance });
        }
    });

    // Sort by distance (nearest first) and return only the marker objects
    return collidingMarkers
        .sort((a, b) => a.distance - b.distance)
        .map(item => item.marker);
};

/**
 * Converts a pixel collision radius to meters using the Google Maps Mercator projection.
 * metersPerPixel = (156543.03392 * cos(lat)) / 2^zoom
 * The total collision diameter is collisionRadius * 2 (sum of both marker radii),
 * so the returned value is the max center-to-center distance in meters for a collision.
 */
export const getCollisionRadiusKm = (
    map: google.maps.Map | null,
    lat: number,
    collisionRadius: number
): number => {
    if (!map) return 0;
    const zoom = map.getZoom() || 1;
    const metersPerPixel = (156543.03392 * Math.cos(lat * Math.PI / 180)) / Math.pow(2, zoom);
    return (collisionRadius * 2 * metersPerPixel) / 1000;
};

// Row style for recommended regions
export const getRegionsRowStyle = (row: any) => {
    const isFiltered = row.original?.rankData?.filteredOut;
    if (isFiltered) {
        return { backgroundColor: 'rgba(0, 0, 0, 0.04)' };
    }
    return { backgroundColor: '#FFFFFF' };
};

// Get the first coordinate [lng, lat] from any GeoJSON geometry
export const getFirstCoordinate = (coordinates: any): [number, number] => {
    if (typeof coordinates[0] === 'number') return coordinates;
    return getFirstCoordinate(coordinates[0]);
};

// Convert lat/lng to screen pixel coordinates relative to the map container
export const latLngToPixel = (map: google.maps.Map, lat: number, lng: number): { x: number; y: number } | null => {
    const projection = map.getProjection();
    const bounds = map.getBounds();
    if (!projection || !bounds) return null;

    const nw = new google.maps.LatLng(
        bounds.getNorthEast().lat(),
        bounds.getSouthWest().lng()
    );
    const scale = Math.pow(2, map.getZoom()!);
    const worldCoordinateNW = projection.fromLatLngToPoint(nw)!;
    const worldCoordinate = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng))!;

    return {
        x: Math.floor((worldCoordinate.x - worldCoordinateNW.x) * scale),
        y: Math.floor((worldCoordinate.y - worldCoordinateNW.y) * scale),
    };
};
